// src/pages/admin/ViewRegistrations.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { getRegistrationsByCompetition, deleteRegistration, Registration } from "../../firebase/registrations";
import { getPaymentByReferenceCode } from "../../firebase/payments";
import { generatePaymentPDF } from "../../utils/generateReceiptPDF";
import { getAllCompetitions } from "../../firebase/competitions";
import { invalidateCachePrefix } from "../../utils/cache";
import { useTranslation } from "../../hooks/useTranslation";
import { useAuth } from "../../context/AuthContext";
import Navbar from "../../components/Navbar";
import EditRegistrationModal from "../../components/EditRegistrationModal";
import RegistrationHistoryModal from "../../components/RegistrationHistoryModal";
import { C, FONT } from "../../theme";
import { formatDate as formatDateLocalized } from "../../utils/dateHelpers";
import * as XLSX from "xlsx";

interface LocationState {
  competitionName?: string;
}

export default function ViewRegistrations() {
  const { competitionId } = useParams<{ competitionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser, userRole } = useAuth();

  const competitionName =
    (location.state as LocationState)?.competitionName ?? "Competition";

  const [registrations, setRegistrations] = useState<(Registration & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingRef, setDownloadingRef] = useState<string | null>(null);

  // ── Filters ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  type PayFilter = "all" | "online" | "prepaid" | "admin_manual";
  const [payFilter, setPayFilter] = useState<PayFilter>("all");

  // Edit modal state
  const [editModal, setEditModal] = useState<{
    open: boolean;
    registration: (Registration & { id: string }) | null;
  }>({ open: false, registration: null });

  // History modal state
  const [historyModal, setHistoryModal] = useState<{
    open: boolean;
    registration: (Registration & { id: string }) | null;
  }>({ open: false, registration: null });

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    registration: (Registration & { id: string }) | null;
  }>({ open: false, registration: null });
  const [deleting, setDeleting] = useState(false);

  // Competition metadata (for weight categories + types in edit modal)
  const [competition, setCompetition] = useState<any | null>(null);

  useEffect(() => {
    if (!competitionId) return;

    const load = async () => {
      setLoading(true);
      try {
        const [data, comps] = await Promise.all([
          getRegistrationsByCompetition(competitionId),
          getAllCompetitions(),
        ]);
        const sorted = [...data].sort((a, b) => {
          const aTime = a.registeredAt?.toDate?.()?.getTime() ?? 0;
          const bTime = b.registeredAt?.toDate?.()?.getTime() ?? 0;
          return bTime - aTime;
        });
        setRegistrations(sorted as (Registration & { id: string })[]);
        const comp = comps.find((c: any) => c.id === competitionId);
        if (comp) setCompetition(comp);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [competitionId]);

  const totalRevenue = registrations.reduce((sum, r) => sum + (r.price ?? 0), 0);

  // ── Filtered registrations (search + payment-type) ─────────────────────────
  const filteredRegs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return registrations.filter((r) => {
      // Payment-type filter
      if (payFilter !== "all") {
        const isManual = (r as any).isFree || (r as any).paymentType === "admin_manual";
        const pt = isManual
          ? "admin_manual"
          : ((r as any).paymentType ?? "online");
        if (pt !== payFilter) return false;
      }
      // Free-text search
      if (q) {
        const name = `${r.athleteSnapshot?.firstName ?? ""} ${r.athleteSnapshot?.lastName ?? ""}`.toLowerCase();
        const ref = (r.referenceCode ?? "").toLowerCase();
        const club = (r.club ?? "").toLowerCase();
        if (!name.includes(q) && !ref.includes(q) && !club.includes(q)) return false;
      }
      return true;
    });
  }, [registrations, search, payFilter]);

  const formatDate = (ts: any): string => formatDateLocalized(ts);

  // Check-in roster export. Always pulls the freshest data (weight changes /
  // removals included), and includes the coach + how each athlete paid plus a
  // blank "Checked In" column so the gate can tick arrivals off on paper.
  const handleExportExcel = async () => {
    if (!competitionId) return;
    invalidateCachePrefix("registrations:");
    let data: (Registration & { id: string })[] = registrations;
    try {
      const fresh = await getRegistrationsByCompetition(competitionId);
      data = [...fresh].sort((a, b) => {
        const aT = a.registeredAt?.toDate?.()?.getTime() ?? 0;
        const bT = b.registeredAt?.toDate?.()?.getTime() ?? 0;
        return bT - aT;
      }) as (Registration & { id: string })[];
      setRegistrations(data); // keep the on-screen table in sync with the export
    } catch {
      // Network hiccup — fall back to what's already on screen.
    }

    const paymentLabel = (r: any): string => {
      if (r.isFree || r.paymentType === "admin_manual") return "Bank";
      if (r.paymentType === "prepaid") return "Prepaid";
      return "Online";
    };
    const coachLabel = (r: any): string =>
      (r.coachName && String(r.coachName).trim()) || r.coachEmail || "—";

    const rows = data.map((r, idx) => ({
      "#": idx + 1,
      "Reference Code": r.referenceCode ?? "—",
      "Athlete": `${r.athleteSnapshot?.firstName ?? ""} ${r.athleteSnapshot?.lastName ?? ""}`.trim(),
      "Date of Birth": r.athleteSnapshot?.dateOfBirth ?? "—",
      "Category": r.category ?? "—",
      "Club": r.club ?? "—",
      "Division": r.division ?? "—",
      "Weight Category": r.weightCategory ?? "—",
      "Coach": coachLabel(r),
      "Payment": paymentLabel(r),
      "Price (GEL)": r.price ?? 0,
      "Submitted": formatDate(r.registeredAt),
      "Checked In": "",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 4 }, { wch: 16 }, { wch: 22 }, { wch: 14 }, { wch: 10 },
      { wch: 22 }, { wch: 10 }, { wch: 16 }, { wch: 20 }, { wch: 10 },
      { wch: 12 }, { wch: 16 }, { wch: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Check-in Roster");
    const safeCompName = competitionName.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 30);
    XLSX.writeFile(wb, `checkin-roster-${safeCompName}.xlsx`);
  };

  const handleDownloadPDF = async (referenceCode: string) => {
    setDownloadingRef(referenceCode);
    try {
      const payment = await getPaymentByReferenceCode(referenceCode);
      if (!payment) {
        alert("Payment record not found for this registration.");
        return;
      }
      await generatePaymentPDF(payment);
    } catch (err) {
      console.error("PDF download failed:", err);
      alert("Failed to generate PDF.");
    } finally {
      setDownloadingRef(null);
    }
  };

  // ── Edit modal callbacks ──────────────────────────────────────────────────

  const handleEditSaved = (
    registrationId: string,
    updates: Partial<Registration>
  ) => {
    setRegistrations((prev) =>
      prev.map((r) =>
        r.id === registrationId ? { ...r, ...updates } : r
      )
    );
  };

  // ── Delete (remove athlete from competition) ──────────────────────────────
  // Writes a "deleted" audit entry and busts the registrations cache (handled
  // inside deleteRegistration), then drops the row from local state so the
  // admin table updates instantly. The public list reflects it on next refresh.
  const handleConfirmDelete = async () => {
    const reg = deleteModal.registration;
    if (!reg || !currentUser) return;
    setDeleting(true);
    try {
      await deleteRegistration(reg.id, {
        editorUid: currentUser.uid,
        editorEmail: currentUser.email ?? "",
        changedBy: userRole === "coach" ? "coach" : "admin",
      });
      setRegistrations((prev) => prev.filter((r) => r.id !== reg.id));
      setDeleteModal({ open: false, registration: null });
    } catch (err) {
      console.error("Failed to delete registration:", err);
      alert("Failed to delete this registration. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={s.page} className="page-content">
      <Navbar />

      {/* Edit modal */}
      {editModal.open && editModal.registration && competition && (
        <EditRegistrationModal
          registration={editModal.registration}
          weightCategories={competition.weightCategories ?? { male: [], female: [] }}
          types={competition.types ?? []}
          onClose={() => setEditModal({ open: false, registration: null })}
          onSaved={(updates) => {
            handleEditSaved(editModal.registration!.id, updates);
            setEditModal({ open: false, registration: null });
          }}
        />
      )}

      {/* History modal */}
      {historyModal.open && historyModal.registration && (
        <RegistrationHistoryModal
          registration={historyModal.registration}
          onClose={() => setHistoryModal({ open: false, registration: null })}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteModal.open && deleteModal.registration && (
        <DeleteRegistrationModal
          registration={deleteModal.registration}
          deleting={deleting}
          t={t}
          onCancel={() => {
            if (!deleting) setDeleteModal({ open: false, registration: null });
          }}
          onConfirm={handleConfirmDelete}
        />
      )}

      <div style={s.container}>
        <button
          type="button"
          style={s.backBtn}
          onClick={() => navigate("/admin/registrations")}
        >
          {t("admin.registrations.backBtn")}
        </button>

        <div style={s.header}>
          <div>
            <div style={s.pageLabel}>{t("admin.registrations.pageLabel")}</div>
            <div style={s.heading}>{competitionName}</div>
            <div style={s.subheading}>{t("admin.registrations.heading")}</div>
          </div>
          {!loading && registrations.length > 0 && (
            <button style={s.exportBtn} onClick={handleExportExcel}>
              ↓ {t("admin.registrations.exportBtn")}
            </button>
          )}
        </div>

        {!loading && registrations.length > 0 && (
          <div style={s.statsRow}>
            <div style={s.statBox}>
              <div style={s.statNumber}>{registrations.length}</div>
              <div style={s.statLabel}>{t("admin.registrations.total.registrations")}</div>
            </div>
            <div style={s.statBox}>
              <div style={{ ...s.statNumber, color: C.red }}>{totalRevenue} GEL</div>
              <div style={s.statLabel}>{t("admin.registrations.total.amount")}</div>
            </div>
            <div style={s.statBox}>
              <div style={{ ...s.statNumber, color: C.blue }}>
                {registrations.filter((r) => r.isFree).length}
              </div>
              <div style={s.statLabel}>Free / Manual</div>
            </div>
          </div>
        )}

        {loading && (
          <div style={s.stateBox}>
            <div style={s.stateText}>{t("admin.registrations.loading")}</div>
          </div>
        )}

        {!loading && registrations.length === 0 && (
          <div style={s.stateBox}>
            <div style={s.emptyIcon}>📋</div>
            <div style={s.stateText}>{t("admin.registrations.empty")}</div>
          </div>
        )}

        {!loading && registrations.length > 0 && (
          <>
            {/* Filter bar — stacks vertically on mobile */}
            <div style={s.filterBar}>
              <input
                type="text"
                placeholder="Search athlete, reference code, or club…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={s.filterSearch}
              />
              <div style={s.chipGroup}>
                {([
                  ["all",          "All"],
                  ["online",       "Online"],
                  ["prepaid",      "Prepaid"],
                  ["admin_manual", "Bank"],
                ] as [PayFilter, string][]).map(([key, label]) => (
                  <button
                    key={key}
                    style={{
                      ...s.chip,
                      ...(payFilter === key ? s.chipActive : {}),
                    }}
                    onClick={() => setPayFilter(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {filteredRegs.length === 0 && (
              <div style={s.stateBox}>
                <div style={s.stateText}>No registrations match these filters.</div>
              </div>
            )}

            {filteredRegs.length > 0 && (
              <>
                <div style={s.resultCount}>
                  {filteredRegs.length} of {registrations.length}
                </div>
                <div style={s.tableCard}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>#</th>
                        <th style={s.th}>{t("admin.registrations.table.ref")}</th>
                        <th style={s.th}>{t("admin.registrations.table.athlete")}</th>
                        <th style={s.th}>{t("admin.registrations.table.dob")}</th>
                        <th style={s.th}>Category</th>
                        <th style={s.th}>{t("admin.registrations.table.club")}</th>
                        <th style={s.th}>{t("admin.registrations.table.division")}</th>
                        <th style={s.th}>{t("admin.registrations.table.weight")}</th>
                        <th style={s.th}>{t("admin.registrations.table.price")}</th>
                        <th style={s.th}>{t("admin.registrations.table.date")}</th>
                        <th style={s.th}>Receipt</th>
                        <th style={s.th}>Edit</th>
                        <th style={s.th}>History</th>
                        <th style={s.th}>{t("admin.registrations.deleteBtn")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRegs.map((r, idx) => (
                  <tr key={r.id} style={s.tableRow}>
                    <td style={{ ...s.td, color: C.text3, fontSize: "12px" }}>{idx + 1}</td>
                    <td style={s.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={s.refCode}>{r.referenceCode ?? "—"}</span>
                        {r.isFree && <span style={s.freeBadge}>FREE</span>}
                      </div>
                    </td>
                    <td style={{ ...s.td, fontWeight: 600, color: C.text }}>
                      {r.athleteSnapshot?.firstName} {r.athleteSnapshot?.lastName}
                    </td>
                    <td style={s.td}>{r.athleteSnapshot?.dateOfBirth ?? "—"}</td>
                    <td style={s.td}>{r.category ?? "—"}</td>
                    <td style={s.td}>{r.club}</td>
                    <td style={s.td}>{r.division}</td>
                    <td style={s.td}>{r.weightCategory}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: C.text }}>{r.price} GEL</td>
                    <td style={{ ...s.td, color: C.text3, fontSize: "12px" }}>
                      {formatDate(r.registeredAt)}
                    </td>
                    <td style={s.td}>
                      {r.isFree ? (
                        <span style={{ fontSize: "11px", color: C.text3 }}>—</span>
                      ) : (
                        <button
                          style={s.pdfBtn}
                          onClick={() => handleDownloadPDF(r.referenceCode)}
                          disabled={downloadingRef === r.referenceCode}
                        >
                          {downloadingRef === r.referenceCode ? "⏳" : "↓ PDF"}
                        </button>
                      )}
                    </td>
                    <td style={s.td}>
                      <button
                        style={s.editBtn}
                        onClick={() =>
                          setEditModal({ open: true, registration: r })
                        }
                      >
                        ✎ Edit
                      </button>
                    </td>
                    <td style={s.td}>
                      <button
                        style={s.editBtn}
                        onClick={() =>
                          setHistoryModal({ open: true, registration: r })
                        }
                        title="View edit history"
                      >
                        🕒 History
                      </button>
                    </td>
                    <td style={s.td}>
                      <button
                        style={s.deleteBtn}
                        onClick={() =>
                          setDeleteModal({ open: true, registration: r })
                        }
                        title="Remove this athlete from the competition"
                      >
                        🗑 {t("admin.registrations.deleteBtn")}
                      </button>
                    </td>
                  </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: {
    maxWidth: "1300px",
    margin: "0 auto",
    padding: "40px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  backBtn: { fontSize: "13px", color: C.text2, cursor: "pointer", display: "inline-block" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
  },
  pageLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    marginBottom: "4px",
    textTransform: "uppercase",
  },
  heading: {
    fontFamily: FONT.display,
    fontSize: "32px",
    letterSpacing: "1px",
    color: C.text,
    lineHeight: 1,
    marginBottom: "4px",
  },
  subheading: { fontSize: "14px", color: C.text2 },
  exportBtn: {
    backgroundColor: C.success,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
  },
  statsRow: { display: "flex", gap: "12px", flexWrap: "wrap" },
  statBox: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
    padding: "12px 22px",
    textAlign: "center",
    minWidth: "100px",
  },
  statNumber: { fontFamily: FONT.display, fontSize: "28px", color: C.text, lineHeight: 1 },
  statLabel: {
    fontSize: "10px",
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginTop: "3px",
  },
  stateBox: { textAlign: "center", padding: "80px 24px" },
  emptyIcon: { fontSize: "40px", marginBottom: "16px" },
  stateText: { fontSize: "16px", color: C.text2 },
  tableCard: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "12px",
    overflow: "auto",
  },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "1100px" },
  th: {
    textAlign: "left",
    padding: "11px 14px",
    backgroundColor: C.surface2,
    color: C.text2,
    fontWeight: 700,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: "nowrap",
  },
  tableRow: { borderBottom: `1px solid ${C.border}` },
  td: {
    padding: "12px 14px",
    color: C.text2,
    fontSize: "13px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  refCode: {
    fontFamily: "monospace",
    fontSize: "12px",
    backgroundColor: C.surface2,
    padding: "3px 7px",
    borderRadius: "4px",
    border: `1px solid ${C.border}`,
    color: C.text,
    fontWeight: 600,
    letterSpacing: "0.5px",
  },
  freeBadge: {
    backgroundColor: "var(--color-info-soft)",
    color: C.blue,
    borderRadius: "4px",
    padding: "1px 5px",
    fontSize: "9px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    border: "1px solid #93c5fd",
  },
  pdfBtn: {
    backgroundColor: C.blue,
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  editBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  deleteBtn: {
    backgroundColor: C.redDim,
    color: C.red,
    border: `1px solid ${C.red}`,
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  // ── Filter bar ──────────────────────────────────────────────────────────
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    alignItems: "center",
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
    padding: "10px 12px",
  },
  filterSearch: {
    flex: "1 1 240px",
    minWidth: 0,
    padding: "9px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "13px",
    backgroundColor: C.surface2,
    color: C.text,
    outline: "none",
    boxSizing: "border-box",
  },
  chipGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  chip: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "20px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    minHeight: "32px",
  },
  chipActive: {
    backgroundColor: C.red,
    color: "#fff",
    borderColor: C.red,
  },
  resultCount: {
    fontSize: "12px",
    color: C.text3,
    fontWeight: 600,
    padding: "0 2px",
  },
};

// ---------------------------------------------------------------------------
// Delete confirmation modal
// ---------------------------------------------------------------------------

interface DeleteRegistrationModalProps {
  registration: Registration & { id: string };
  deleting: boolean;
  t: (key: string) => string;
  onCancel: () => void;
  onConfirm: () => void;
}

function DeleteRegistrationModal({
  registration,
  deleting,
  t,
  onCancel,
  onConfirm,
}: DeleteRegistrationModalProps) {
  const athleteName = `${registration.athleteSnapshot?.firstName ?? ""} ${registration.athleteSnapshot?.lastName ?? ""}`.trim();
  return (
    <div style={dm.overlay} onClick={onCancel}>
      <div style={dm.modal} onClick={(e) => e.stopPropagation()}>
        <div style={dm.accentBar} />
        <div style={dm.body}>
          <div style={dm.title}>{t("admin.registrations.delete.title")}</div>
          <div style={dm.athleteName}>{athleteName}</div>
          <div style={dm.msg}>
            {t("admin.registrations.delete.msg").replace("{name}", athleteName)}
          </div>
          <div style={dm.actions}>
            <button style={dm.cancelBtn} onClick={onCancel} disabled={deleting}>
              {t("admin.registrations.delete.cancel")}
            </button>
            <button
              style={deleting ? dm.confirmBtnDisabled : dm.confirmBtn}
              onClick={onConfirm}
              disabled={deleting}
            >
              {deleting
                ? t("admin.registrations.delete.deleting")
                : `🗑 ${t("admin.registrations.delete.confirm")}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const dm: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.55)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  modal: {
    backgroundColor: C.surface,
    borderRadius: "12px",
    width: "100%",
    maxWidth: "440px",
    overflow: "hidden",
    boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
  },
  accentBar: { height: "4px", backgroundColor: C.red },
  body: { padding: "24px" },
  title: {
    fontFamily: FONT.display,
    fontSize: "22px",
    color: C.text,
    letterSpacing: "0.5px",
    marginBottom: "8px",
  },
  athleteName: { fontSize: "15px", fontWeight: 700, color: C.red, marginBottom: "10px" },
  msg: { fontSize: "13px", color: C.text2, lineHeight: 1.6, marginBottom: "20px" },
  actions: { display: "flex", gap: "10px", justifyContent: "flex-end" },
  cancelBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    padding: "10px 20px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: 500,
  },
  confirmBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "10px 22px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  confirmBtnDisabled: {
    backgroundColor: C.border,
    color: C.text3,
    border: "none",
    borderRadius: "7px",
    padding: "10px 22px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "not-allowed",
  },
};
